const ANON_ID_KEY = 'cls_plus_freshchat_anon_id'
const LAST_MATCH_KEY = 'cls_plus_freshchat_last_match'
const MATCH_LABEL_CACHE_KEY = 'cls_plus_freshchat_match_label_cache'

const DEFAULT_LAST_MATCH_TTL_MS = 10 * 60 * 1000
const DEFAULT_MATCH_LABEL_TTL_MS = 24 * 60 * 60 * 1000

type LastMatchContext = {
  matchId: string
  mode?: string
  leftAt: number
}

type MatchLabelCache = Record<string, { label: string; updatedAt: number }>

const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) return null
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

const safeJsonStringify = (value: unknown): string | null => {
  try {
    return JSON.stringify(value)
  } catch {
    return null
  }
}

const randomId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`
}

export const getOrCreateAnonId = () => {
  const existing = window.localStorage.getItem(ANON_ID_KEY)
  if (existing) return existing
  const anonId = `anon_${randomId()}`
  window.localStorage.setItem(ANON_ID_KEY, anonId)
  return anonId
}

export const setLastMatchContext = (matchId: string, mode: string | undefined) => {
  if (!matchId) return
  const context: LastMatchContext = {
    matchId,
    mode,
    leftAt: Date.now(),
  }
  window.localStorage.setItem(LAST_MATCH_KEY, JSON.stringify(context))
}

export const getLastMatchContext = (ttlMs: number = DEFAULT_LAST_MATCH_TTL_MS): LastMatchContext | null => {
  const context = safeJsonParse<LastMatchContext>(window.localStorage.getItem(LAST_MATCH_KEY))
  if (!context?.matchId || !context.leftAt) return null

  const expired = Date.now() - context.leftAt > ttlMs
  if (expired) {
    window.localStorage.removeItem(LAST_MATCH_KEY)
    return null
  }

  return context
}

export const clearLastMatchContext = () => {
  window.localStorage.removeItem(LAST_MATCH_KEY)
}

export const setCachedMatchLabel = (matchId: string, label: string) => {
  if (!matchId) return
  if (!label) return

  const existing = safeJsonParse<MatchLabelCache>(window.localStorage.getItem(MATCH_LABEL_CACHE_KEY)) || {}
  existing[matchId] = { label, updatedAt: Date.now() }

  const serialized = safeJsonStringify(existing)
  if (!serialized) return
  window.localStorage.setItem(MATCH_LABEL_CACHE_KEY, serialized)
}

export const getCachedMatchLabel = (matchId: string, ttlMs: number = DEFAULT_MATCH_LABEL_TTL_MS): string | null => {
  if (!matchId) return null
  const cache = safeJsonParse<MatchLabelCache>(window.localStorage.getItem(MATCH_LABEL_CACHE_KEY))
  if (!cache) return null

  const entry = cache[matchId]
  if (!entry?.label || !entry.updatedAt) return null

  const expired = Date.now() - entry.updatedAt > ttlMs
  if (expired) {
    try {
      delete cache[matchId]
      const serialized = safeJsonStringify(cache)
      if (serialized) window.localStorage.setItem(MATCH_LABEL_CACHE_KEY, serialized)
    } catch {
      // ignore
    }
    return null
  }

  return entry.label
}
